import { ExportOutlined } from "@ant-design/icons";
import { DockerTokenVault } from "@p0security/integrations-docker-shared/components";
import {
  VAULT_OUTSIDE_CONNECTOR_CLOUD,
  renderHosting,
} from "@p0security/integrations-docker-shared/hosting";
import type {
  DockerConnectorHosting,
  DockerOrganizationConfig,
} from "@p0security/integrations-docker-shared/types";
import { shellLines } from "@p0security/integrations-vaulted-credential-shared";
import {
  Alert,
  Button,
  CommandDisplay,
  Flex,
  Steps,
  Typography,
} from "@p0security/ui";
import { assertNever } from "@p0security/util/assertNever";
import type { ComponentProps, ReactNode } from "react";

import { PLACEHOLDER_DOMAIN, toEmailDomain } from "./connector-iac.ts";
import type { ReadGrantBlock } from "./organization-iac.ts";

const { Paragraph, Text } = Typography;

type Vault = DockerOrganizationConfig["vault"];

/** What an organization's page can show of its read grant. */
export type ReadGrant =
  | {
      status: "ready";
      /** The Terraform that lets the connector read the token, in the order
       * to apply it */
      blocks: ReadGrantBlock[];
      hosting: DockerConnectorHosting;
      /** The AWS account that holds the secret, when it isn't the
       * connector's */
      vaultAccountId: string | undefined;
    }
  /** The organization's connector isn't installed */
  | { status: "no-connector" }
  /** The secret isn't in the connector's cloud, so no Terraform grants it */
  | { status: "vault-outside-cloud"; hosting: DockerConnectorHosting };

/** One of the steps that install an organization. */
type Step = { content: ReactNode; status?: "error" | "finish"; title: string };

/** Docker Home, where an organization's owner generates its access token. */
const DOCKER_HOME_URL = "https://app.docker.com";

/** The customer-managed KMS key for a secret in another account than the
 * connector's, in the command that creates the secret. */
const KMS_KEY_ARN = "<kms-key-arn>";

// Reads the token without echoing it or leaving it in the shell's history.
// Each command below takes it from standard input, so it's never an argument,
// which other users of the machine could list.
const READ_TOKEN = "read -rs DOCKER_TOKEN";

const CLEAR_TOKEN = "unset DOCKER_TOKEN";

const pipeToken = (...lines: string[]) =>
  `printf %s "$DOCKER_TOKEN" | ${shellLines(...lines)}`;

/** The secret manager a vault is in, as its option is labelled. */
const toVaultLabel = (vault: Vault) =>
  DockerTokenVault.options[vault.type].label;

/** How to store the token as the organization's secret: where the secret is,
 * such as "account 123456789012 and region us-west-2", the command that
 * creates it, and the command for a secret that already exists. */
const toStoreInstructions = (
  { tokenSecretName: name, vault }: DockerOrganizationConfig,
  { crossAccount }: { crossAccount: boolean }
): { create: string; location: string; update: string } => {
  switch (vault.type) {
    case "aws-sm":
      return {
        create: pipeToken(
          "aws secretsmanager create-secret",
          `--name ${name}`,
          ...(crossAccount ? [`--kms-key-id ${KMS_KEY_ARN}`] : []),
          "--secret-string file:///dev/stdin",
          `--region ${vault.secretsRegion}`
        ),
        location: `account ${vault.install} and region ${vault.secretsRegion}`,
        update: pipeToken(
          "aws secretsmanager put-secret-value",
          `--secret-id ${name}`,
          "--secret-string file:///dev/stdin",
          `--region ${vault.secretsRegion}`
        ),
      };
    case "gcp-sm":
      return {
        create: pipeToken(
          `gcloud secrets create ${name}`,
          `--project ${vault.install}`,
          "--data-file=-"
        ),
        location: `project ${vault.install}`,
        update: pipeToken(
          `gcloud secrets versions add ${name}`,
          `--project ${vault.install}`,
          "--data-file=-"
        ),
      };
    default:
      throw assertNever(vault);
  }
};

/** Whether the connector's allowed-domain `pattern` lets it act for
 * `domain`, as the connector tests it: unanchored, against the part of an
 * email after the "@". A pattern that doesn't compile allows nothing. */
export const allowsDomain = ({
  domain,
  pattern,
}: {
  domain: string;
  pattern: string;
}) => {
  try {
    return new RegExp(pattern.trim()).test(domain);
  } catch {
    return false;
  }
};

/** Says how to store the token as the organization's secret, with the
 * commands that do. */
const StoreToken: React.FC<{
  crossAccount: boolean;
  item: DockerOrganizationConfig;
}> = ({ crossAccount, item }) => {
  const { tokenSecretName: name, vault } = item;
  const { create, location, update } = toStoreInstructions(item, {
    crossAccount,
  });
  return (
    <Flex gap={8} vertical>
      <Text>
        Store the token in {toVaultLabel(vault)} as the secret{" "}
        <Text code>{name}</Text>, in {location}.{" "}
        {crossAccount && (
          <>
            The connector runs in another AWS account, so the secret needs a
            customer-managed KMS key: replace <Text code>{KMS_KEY_ARN}</Text>{" "}
            with that key&apos;s ARN.{" "}
          </>
        )}
        Run this first, paste the token and press Enter. It reads the token
        without showing it:
      </Text>
      <CommandDisplay commands={READ_TOKEN} />
      <Text>Then create the secret:</Text>
      <CommandDisplay commands={create} />
      <Text>
        If the secret already exists, store the token as a new version instead:
      </Text>
      <CommandDisplay commands={update} />
      <Text>Then clear the token from your shell:</Text>
      <CommandDisplay commands={CLEAR_TOKEN} />
      <Text>
        P0 never reads the token. Only your connector does. To rotate it later,
        store the new token the same way, as a new version of this secret.
      </Text>
    </Flex>
  );
};

/** The step that says the organization's connector is deployed. */
const toDeployedStep = (
  connectorId: string,
  hosting: DockerConnectorHosting
): Omit<Step, "title"> => ({
  content: (
    <Text>
      Done: the connector {connectorId} runs as {renderHosting(hosting)}. It
      acts only for the email domains its deployment allows.
    </Text>
  ),
  status: "finish",
});

/** Says where to apply the Terraform that lets the connector read the
 * token. */
const GrantRead: React.FC<{
  /** The AWS account that holds the secret, when it isn't the connector's */
  vaultAccountId: string | undefined;
}> = ({ vaultAccountId }) => (
  <Text>
    Apply the Terraform below. It lets the connector read this one secret, and
    no other.
    {vaultAccountId && (
      <>
        {" "}
        The secret and the connector are in different AWS accounts, so there are
        two configurations: one for the connector&apos;s account, and one for
        account {vaultAccountId}, which holds the secret.
      </>
    )}
  </Text>
);

/** What the steps that depend on the organization's connector show, for each
 * state of its read grant: the connector's own step, what the step that
 * grants the read says, and whether the secret is in another AWS account than
 * the connector. */
const toConnectorSteps = (
  connectorId: string,
  grant: ReadGrant
): { crossAccount: boolean; deploy: Omit<Step, "title">; read: ReactNode } => {
  switch (grant.status) {
    case "no-connector":
      return {
        crossAccount: false,
        deploy: {
          content: (
            <Alert
              message={`The connector ${connectorId} isn't installed. Install it again under Connector, with the same ID and hosting, or remove this organization and add it again with another connector.`}
              showIcon
              type="error"
            />
          ),
          status: "error",
        },
        read: <Text>Install the connector first.</Text>,
      };
    case "ready":
      return {
        crossAccount: grant.vaultAccountId !== undefined,
        deploy: toDeployedStep(connectorId, grant.hosting),
        read: <GrantRead vaultAccountId={grant.vaultAccountId} />,
      };
    case "vault-outside-cloud":
      return {
        crossAccount: false,
        deploy: toDeployedStep(connectorId, grant.hosting),
        read: (
          <Alert
            message={`${VAULT_OUTSIDE_CONNECTOR_CLOUD} Remove this organization and add it again.`}
            showIcon
            type="error"
          />
        ),
      };
    default:
      throw assertNever(grant);
  }
};

/** Says what "Next" checks, and that "Finish" on the next page completes the
 * install. Once the connector has reported its allowed domains, it warns when
 * they leave out the admin's own. */
const Validate: React.FC<{
  adminEmail: string | null | undefined;
  allowedDomains: string | undefined;
  org: string;
}> = ({ adminEmail, allowedDomains, org }) => {
  const domain = toEmailDomain(adminEmail);
  return (
    <Flex gap={8} vertical>
      <Text>
        Click &quot;Next&quot;. P0 asks the connector to sign in to Docker Hub
        with the token, list {org}&apos;s members and try an invite as a dry
        run, which sends nothing and uses no seat. If a check fails, P0 says
        which one. If the connector is denied the secret right after you apply
        the Terraform, wait a few seconds and click &quot;Next&quot; again.
      </Text>
      <Text>
        When the checks pass, click &quot;Finish&quot; on the next page. {org}{" "}
        can&apos;t be requested until you do.
      </Text>
      {allowedDomains &&
        domain &&
        !allowsDomain({ domain, pattern: allowedDomains }) && (
          <Alert
            message={`The connector allows the email domains ${allowedDomains}, which leaves out your own, ${domain}. If people at ${domain} should be able to request roles in ${org}, change domain_allow_pattern in the connector's Terraform and apply it.`}
            showIcon
            type="warning"
          />
        )}
    </Flex>
  );
};

/**
 * The steps that install one Docker Hub organization, in the order the
 * customer takes them, shown above the organization's Terraform.
 *
 * The connector is deployed before the organization is added, since the
 * organization's form lists only installed connectors, so that step shows as
 * done, or as failed if the connector has since been removed.
 */
export const OrganizationSteps: React.FC<{
  /** The signed-in admin's email, whose domain the allowed domains are
   * checked against */
  adminEmail: string | null | undefined;
  grant: ReadGrant;
  item: DockerOrganizationConfig;
  /** The Docker Hub organization's name */
  org: string;
}> = ({ adminEmail, grant, item, org }) => {
  const { crossAccount, deploy, read } = toConnectorSteps(
    item.connector,
    grant
  );
  const steps: Step[] = [
    {
      content: (
        <Flex gap={8} vertical>
          <Text>
            Organization access tokens need a Docker Team or Business plan. In
            Docker Home, open {org}, go to Identity &amp; auth, then Access
            tokens, and click Generate access token. Under the
            organization&apos;s scopes, select <Text strong>Member Edit</Text>{" "}
            and <Text strong>Invite Edit</Text>. Select no repository scopes: P0
            changes who has which role in {org}, and nothing in its
            repositories. Docker shows the token once, so copy it.
          </Text>
          <div>
            <Button
              href={DOCKER_HOME_URL}
              icon={<ExportOutlined />}
              rel="noopener noreferrer"
              target="_blank"
            >
              Open Docker Home
            </Button>
          </div>
        </Flex>
      ),
      title: "Create the organization access token",
    },
    {
      content: <StoreToken crossAccount={crossAccount} item={item} />,
      title: `Store the token in ${toVaultLabel(item.vault)}`,
    },
    { ...deploy, title: "Deploy the connector" },
    { content: read, title: "Let the connector read the token" },
    {
      content: (
        <Validate
          adminEmail={adminEmail}
          allowedDomains={item.allowedDomains}
          org={org}
        />
      ),
      title: "Validate",
    },
  ];
  const items: ComponentProps<typeof Steps>["items"] = steps.map(
    ({ content, status, title }) => ({
      description: content,
      status: status ?? "process",
      title,
    })
  );
  return <Steps direction="vertical" items={items} />;
};

/** What to do with a connector's Terraform, shown above it. */
export const ConnectorHelp: React.FC<{
  /** The signed-in admin's email, whose domain the Terraform's allowed-domain
   * pattern starts as */
  adminEmail: string | null | undefined;
  connectorId: string;
  hosting: DockerConnectorHosting;
}> = ({ adminEmail, connectorId, hosting }) => (
  <>
    <Paragraph>
      The Terraform below deploys the connector {connectorId} as{" "}
      {renderHosting(hosting)}, and lets P0 invoke it. The connector reaches
      Docker Hub over the internet, so it needs no VPC.
    </Paragraph>
    <Paragraph>
      Before you apply it, set <Text code>domain_allow_pattern</Text> to the
      email domains the connector may act for.{" "}
      {toEmailDomain(adminEmail)
        ? "P0 fills in your own email's domain as a start."
        : `P0 doesn't know your email's domain, so it fills in ${PLACEHOLDER_DOMAIN}, which matches nobody: replace it.`}{" "}
      The pattern lives only in your deployment: P0 can&apos;t change it, and
      shows the one deployed on each organization as &quot;Allowed
      domains&quot;.
    </Paragraph>
    <Paragraph>
      Then click &quot;Next&quot;, and P0 checks that it can invoke the
      connector. When it can, click &quot;Finish&quot; on the next page:
      organizations can&apos;t name the connector until you do. Each
      organization&apos;s own Terraform then lets the connector read that
      organization&apos;s access token.
    </Paragraph>
  </>
);
